import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';

import { Timestamp } from 'firebase/firestore';

import { AthleteWithId, PerCoachCommentEntry, SkillValues } from '../../types/athlete';
import { parsePerCoachComments } from '../../services/athleteService';
import { GENERAL_COMMENTS_FIELD_ID } from '../../types/formSchema';
import { clearOverlay, emptyOverlay, loadOverlay, saveOverlay } from './overlay';
import {
  ADD_ATHLETE_PREFILL,
  buildSeedAthletes,
  SEED_COACH,
  SEED_TABBED_ATHLETE_IDS,
  SEED_TRYOUT_META,
} from './seed';
import {
  AthleteOverlay,
  DemoOverlay,
  DemoStoreActions,
  DemoStoreSnapshot,
  UserAddedAthlete,
} from './types';

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

type Action =
  | { type: 'addOrReplaceUserAthlete'; athlete: UserAddedAthlete }
  | { type: 'setScoreField'; athleteId: string; fieldId: string; value: SkillValues[string] }
  | { type: 'setTeamPlacement'; athleteId: string; teamPlacement: string | null }
  | { type: 'addGeneralComment'; athleteId: string; entry: PerCoachCommentEntry }
  | { type: 'editGeneralComment'; athleteId: string; commentId: string; newText: string }
  | { type: 'deleteGeneralComment'; athleteId: string; commentId: string }
  | { type: 'setTabbedAthletes'; next: string[] }
  | { type: 'setCoachName'; name: string }
  | { type: 'reset' };

const SEED_USER_ATHLETE_ID = 'demo-user-1';

function reducer(state: DemoOverlay, action: Action): DemoOverlay {
  switch (action.type) {
    case 'addOrReplaceUserAthlete':
      return { ...state, userAddedAthlete: action.athlete };

    case 'setScoreField': {
      // User-added athlete: edit lives on the athlete record itself.
      if (state.userAddedAthlete && state.userAddedAthlete.id === action.athleteId) {
        return {
          ...state,
          userAddedAthlete: {
            ...state.userAddedAthlete,
            scores: { ...state.userAddedAthlete.scores, [action.fieldId]: action.value },
          },
        };
      }
      const prev = state.athleteOverlays[action.athleteId] ?? {};
      const nextScores: SkillValues = { ...(prev.scores ?? {}), [action.fieldId]: action.value };
      return {
        ...state,
        athleteOverlays: {
          ...state.athleteOverlays,
          [action.athleteId]: { ...prev, scores: nextScores },
        },
      };
    }

    case 'setTeamPlacement': {
      if (state.userAddedAthlete && state.userAddedAthlete.id === action.athleteId) {
        return {
          ...state,
          userAddedAthlete: { ...state.userAddedAthlete, teamPlacement: action.teamPlacement },
        };
      }
      const prev = state.athleteOverlays[action.athleteId] ?? {};
      return {
        ...state,
        athleteOverlays: {
          ...state.athleteOverlays,
          [action.athleteId]: { ...prev, teamPlacement: action.teamPlacement },
        },
      };
    }

    case 'addGeneralComment': {
      if (state.userAddedAthlete && state.userAddedAthlete.id === action.athleteId) {
        const existingRaw = state.userAddedAthlete.scores[GENERAL_COMMENTS_FIELD_ID];
        const existing = Array.isArray(existingRaw) ? (existingRaw as PerCoachCommentEntry[]) : [];
        return {
          ...state,
          userAddedAthlete: {
            ...state.userAddedAthlete,
            scores: {
              ...state.userAddedAthlete.scores,
              [GENERAL_COMMENTS_FIELD_ID]: [...existing, action.entry],
            },
          },
        };
      }
      const prev = state.athleteOverlays[action.athleteId] ?? {};
      const existing = prev.generalComments ?? [];
      return {
        ...state,
        athleteOverlays: {
          ...state.athleteOverlays,
          [action.athleteId]: { ...prev, generalComments: [...existing, action.entry] },
        },
      };
    }

    case 'editGeneralComment': {
      if (state.userAddedAthlete && state.userAddedAthlete.id === action.athleteId) {
        const existingRaw = state.userAddedAthlete.scores[GENERAL_COMMENTS_FIELD_ID];
        const existing = Array.isArray(existingRaw) ? (existingRaw as PerCoachCommentEntry[]) : [];
        const updated = existing.map((e) =>
          e.id === action.commentId ? { ...e, text: action.newText } : e,
        );
        return {
          ...state,
          userAddedAthlete: {
            ...state.userAddedAthlete,
            scores: { ...state.userAddedAthlete.scores, [GENERAL_COMMENTS_FIELD_ID]: updated },
          },
        };
      }
      const prev = state.athleteOverlays[action.athleteId] ?? {};
      const existing = prev.generalComments ?? [];
      return {
        ...state,
        athleteOverlays: {
          ...state.athleteOverlays,
          [action.athleteId]: {
            ...prev,
            generalComments: existing.map((e) =>
              e.id === action.commentId ? { ...e, text: action.newText } : e,
            ),
          },
        },
      };
    }

    case 'deleteGeneralComment': {
      if (state.userAddedAthlete && state.userAddedAthlete.id === action.athleteId) {
        const existingRaw = state.userAddedAthlete.scores[GENERAL_COMMENTS_FIELD_ID];
        const existing = Array.isArray(existingRaw) ? (existingRaw as PerCoachCommentEntry[]) : [];
        return {
          ...state,
          userAddedAthlete: {
            ...state.userAddedAthlete,
            scores: {
              ...state.userAddedAthlete.scores,
              [GENERAL_COMMENTS_FIELD_ID]: existing.filter(
                (e) => e.id !== action.commentId,
              ),
            },
          },
        };
      }
      const prev = state.athleteOverlays[action.athleteId] ?? {};
      const existing = prev.generalComments ?? [];
      return {
        ...state,
        athleteOverlays: {
          ...state.athleteOverlays,
          [action.athleteId]: {
            ...prev,
            generalComments: existing.filter(
              (e) => e.id !== action.commentId,
            ),
          },
        },
      };
    }

    case 'setTabbedAthletes': {
      const seen = new Set<string>();
      const deduped: string[] = [];
      for (const id of action.next) {
        if (typeof id === 'string' && id && !seen.has(id)) {
          seen.add(id);
          deduped.push(id);
        }
      }
      // Once the user touches the list, the override fully replaces seed defaults.
      return { ...state, tabbedAthletesOverride: deduped };
    }

    case 'setCoachName': {
      const trimmed = action.name.trim();
      return { ...state, coachNameOverride: trimmed.length > 0 ? trimmed : null };
    }

    case 'reset':
      return emptyOverlay();

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Merge: seed + overlay → displayed athletes
// ---------------------------------------------------------------------------

function mergeAthlete(seed: AthleteWithId, overlay: AthleteOverlay | undefined): AthleteWithId {
  if (!overlay) return seed;
  const mergedScores: SkillValues = { ...seed.scores, ...(overlay.scores ?? {}) };
  const hasScoresCommentsOverride = overlay.scores?.[GENERAL_COMMENTS_FIELD_ID] !== undefined;
  if (
    !hasScoresCommentsOverride
    && overlay.generalComments
    && overlay.generalComments.length > 0
  ) {
    const seedComments = parsePerCoachComments(seed.scores[GENERAL_COMMENTS_FIELD_ID]);
    mergedScores[GENERAL_COMMENTS_FIELD_ID] = [...seedComments, ...overlay.generalComments];
  }
  return {
    ...seed,
    scores: mergedScores,
    teamPlacement: overlay.teamPlacement !== undefined ? overlay.teamPlacement : seed.teamPlacement,
  };
}

function userAthleteToAthleteWithId(u: UserAddedAthlete): AthleteWithId {
  return {
    id: u.id,
    name: u.name,
    number: u.number,
    position: u.position,
    returningstatus: u.returningstatus,
    createdAt: Timestamp.fromMillis(u.createdAtMillis),
    createdBy: SEED_COACH.uid,
    scores: u.scores,
    teamPlacement: u.teamPlacement,
    imageUrl: u.imageUrl,
    imageThumbnailUrl: u.imageThumbnailUrl,
  };
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface DemoStoreContextValue extends DemoStoreSnapshot, DemoStoreActions {
  addAthletePrefill: typeof ADD_ATHLETE_PREFILL;
}

const DemoStoreContext = createContext<DemoStoreContextValue | null>(null);

export function useDemoStore(): DemoStoreContextValue {
  const ctx = useContext(DemoStoreContext);
  if (!ctx) throw new Error('useDemoStore must be used within DemoStoreProvider');
  return ctx;
}

export function DemoStoreProvider({ children }: { children: React.ReactNode }) {
  const [overlay, dispatch] = useReducer(reducer, undefined as unknown as DemoOverlay, () => loadOverlay());

  // Persist to localStorage on every change. Skip the very first run if it
  // matches what we just loaded (avoids unnecessary writes on mount).
  const firstWriteRef = useRef(true);
  useEffect(() => {
    if (firstWriteRef.current) {
      firstWriteRef.current = false;
      return;
    }
    saveOverlay(overlay);
  }, [overlay]);

  const seedAthletes = useMemo(() => buildSeedAthletes(), []);

  const athletes = useMemo<AthleteWithId[]>(() => {
    const merged = seedAthletes.map((seed) => mergeAthlete(seed, overlay.athleteOverlays[seed.id]));
    if (overlay.userAddedAthlete) {
      merged.push(userAthleteToAthleteWithId(overlay.userAddedAthlete));
    }
    return merged;
  }, [seedAthletes, overlay]);

  const userAddedAthleteId = overlay.userAddedAthlete?.id ?? null;

  const addOrReplaceUserAthlete = useCallback<DemoStoreActions['addOrReplaceUserAthlete']>(
    (input) => {
      const next: UserAddedAthlete = {
        id: SEED_USER_ATHLETE_ID,
        name: input.name,
        number: input.number,
        position: input.position,
        returningstatus: input.returningstatus,
        imageUrl: input.imageUrl,
        imageThumbnailUrl: input.imageThumbnailUrl,
        createdAtMillis: Date.now(),
        scores: {},
        teamPlacement: null,
      };
      dispatch({ type: 'addOrReplaceUserAthlete', athlete: next });
      return next.id;
    },
    [],
  );

  const setScoreField = useCallback<DemoStoreActions['setScoreField']>(
    (athleteId, fieldId, value) => dispatch({ type: 'setScoreField', athleteId, fieldId, value }),
    [],
  );

  const setTeamPlacement = useCallback<DemoStoreActions['setTeamPlacement']>(
    (athleteId, teamPlacement) => dispatch({ type: 'setTeamPlacement', athleteId, teamPlacement }),
    [],
  );

  const addGeneralComment = useCallback<DemoStoreActions['addGeneralComment']>(
    (athleteId, entry) => {
      const athlete = athletes.find((a) => a.id === athleteId);
      const existing = athlete
        ? parsePerCoachComments(athlete.scores[GENERAL_COMMENTS_FIELD_ID])
        : [];
      const fullEntry: PerCoachCommentEntry = { ...entry, createdAt: Timestamp.now() };
      dispatch({
        type: 'setScoreField',
        athleteId,
        fieldId: GENERAL_COMMENTS_FIELD_ID,
        value: [...existing, fullEntry],
      });
    },
    [athletes],
  );

  const editGeneralComment = useCallback<DemoStoreActions['editGeneralComment']>(
    (athleteId, commentId, newText) => {
      const athlete = athletes.find((a) => a.id === athleteId);
      if (!athlete) return;
      const existing = parsePerCoachComments(athlete.scores[GENERAL_COMMENTS_FIELD_ID]);
      const updated = existing.map((e) =>
        e.id === commentId ? { ...e, text: newText } : e,
      );
      dispatch({
        type: 'setScoreField',
        athleteId,
        fieldId: GENERAL_COMMENTS_FIELD_ID,
        value: updated,
      });
    },
    [athletes],
  );

  const deleteGeneralComment = useCallback<DemoStoreActions['deleteGeneralComment']>(
    (athleteId, commentId) => {
      const athlete = athletes.find((a) => a.id === athleteId);
      if (!athlete) return;
      const existing = parsePerCoachComments(athlete.scores[GENERAL_COMMENTS_FIELD_ID]);
      const updated = existing.filter((e) => e.id !== commentId);
      dispatch({
        type: 'setScoreField',
        athleteId,
        fieldId: GENERAL_COMMENTS_FIELD_ID,
        value: updated,
      });
    },
    [athletes],
  );

  const setTabbedAthletes = useCallback<DemoStoreActions['setTabbedAthletes']>(
    (next) => dispatch({ type: 'setTabbedAthletes', next }),
    [],
  );

  const setCoachName = useCallback<DemoStoreActions['setCoachName']>(
    (name) => dispatch({ type: 'setCoachName', name }),
    [],
  );

  const resetDemo = useCallback<DemoStoreActions['resetDemo']>(() => {
    clearOverlay();
    dispatch({ type: 'reset' });
  }, []);

  const coach = useMemo(
    () => ({
      ...SEED_COACH,
      name: overlay.coachNameOverride ?? SEED_COACH.name,
    }),
    [overlay.coachNameOverride],
  );

  // Effective tabbed list: user override (if set) wins; otherwise seed defaults.
  const tabbedAthletes = overlay.tabbedAthletesOverride ?? SEED_TABBED_ATHLETE_IDS;

  const value = useMemo<DemoStoreContextValue>(
    () => ({
      tryoutMeta: SEED_TRYOUT_META,
      coach,
      athletes,
      userAddedAthleteId,
      tabbedAthletes,
      addAthletePrefill: ADD_ATHLETE_PREFILL,
      addOrReplaceUserAthlete,
      setScoreField,
      setTeamPlacement,
      addGeneralComment,
      editGeneralComment,
      deleteGeneralComment,
      setTabbedAthletes,
      setCoachName,
      resetDemo,
    }),
    [
      coach,
      athletes,
      userAddedAthleteId,
      tabbedAthletes,
      addOrReplaceUserAthlete,
      setScoreField,
      setTeamPlacement,
      addGeneralComment,
      editGeneralComment,
      deleteGeneralComment,
      setTabbedAthletes,
      setCoachName,
      resetDemo,
    ],
  );

  return <DemoStoreContext.Provider value={value}>{children}</DemoStoreContext.Provider>;
}
